import { z } from 'zod';

import { SIGN_TYPES } from './signers';

/**
 * A signer configured on an eSign Template step.
 *
 * `userId` is the join to the template: SignCare stores each signer's signature
 * COORDINATES in the template's `signer_info`, keyed by this id, and matches them
 * back by id when the signature request is created
 * (CreateExternalTemplateDocumentRequestHandler). Change the id and the signature
 * loses its position on the page.
 */
export const esignSignerSchema = z.object({
  userId: z.string(),
  name: z.string(),
  /** SignCare SignType as a string: 1 Electronic, 2 Aadhaar, 3 Digital (DSC). */
  signType: z.string(),
  /** How many signature boxes this signer has on the document. */
  boxCount: z.number().int().default(0),
  /**
   * WHO this is, which decides where their email comes from at run time.
   *
   * `applicant` — the person running the session. Their address is the session's own,
   *   so it cannot be known when the workflow is built. They sign in the session.
   * `fixed`     — the same person on every run (a company signatory, a witness).
   *   SignCare sends them their link; the session does not wait for them.
   *
   * SignCare needs ONE way to reach every signer — email or mobile — because that is
   * how the signing link and the sign OTP are delivered. A signer it cannot reach
   * cannot be sent.
   */
  source: z.enum(['applicant', 'fixed']).default('applicant'),
  /**
   * Which channel a `fixed` signer is reached on. Email also delivers the signed
   * copy back, which SMS cannot — so it stays the default.
   *
   * The applicant has no channel here: theirs is decided at run time by what the
   * session knows about them (an emailed invite carries an address, a common link
   * carries the OTP-verified mobile).
   */
  channel: z.enum(['email', 'mobile']).default('email'),
  /** Only for `fixed` signers on the email channel. */
  email: z.string().default(''),
  /** Only for `fixed` signers on the mobile channel. Bare 10-digit number. */
  mobile: z.string().default(''),
});

export type EsignSigner = z.infer<typeof esignSignerSchema>;

/** The signer list as the signature editor hands it back through the URL. */
export const esignSignersSchema = z.array(esignSignerSchema);

/**
 * What an eSign Template step stores in `WorkflowStep.Settings`.
 *
 * The signers and their METHODS live here because they drive the step's price; the
 * coordinates live on the SignCare template because that is where the renderer
 * reads them from.
 */
export const esignStepSettingsSchema = z.object({
  signers: z.array(esignSignerSchema).default([]),
  /** Invite signers one after another rather than all at once. */
  sequentialSigning: z.boolean().default(false),
});

export type EsignStepSettings = z.infer<typeof esignStepSettingsSchema>;

const EMPTY: EsignStepSettings = { signers: [], sequentialSigning: false };

export function parseEsignStepSettings(raw: string | null): EsignStepSettings {
  if (!raw) return EMPTY;

  try {
    const parsed = esignStepSettingsSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : EMPTY;
  } catch {
    return EMPTY;
  }
}

/**
 * Merges eSign settings back into the step's settings JSON, preserving keys this
 * feature does not own (the generic settings tab shares the same blob).
 */
export function writeEsignStepSettings(
  raw: string | null,
  next: Partial<EsignStepSettings>,
): string {
  let base: Record<string, unknown> = {};
  if (raw) {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') base = parsed as Record<string, unknown>;
    } catch {
      /* unparseable — start clean rather than lose the signers */
    }
  }

  return JSON.stringify({ ...base, ...next });
}

/**
 * The step's price: the sum of the methods of the signers who actually sign.
 *
 * Two rules, both from how eSign is really billed:
 *
 *  - Only a signer with a signature PLACED on the document counts. A signer row with
 *    nowhere to sign costs nothing, so a step that has just been added is free and
 *    the price rises as signatures are placed.
 *  - A signer counts ONCE however many boxes they have — signing in three places is
 *    one signature request, not three.
 *
 * An Aadhaar signature and an electronic one are different products at different
 * prices (Console: 25 vs 15). Prices come from the catalogue; this only adds up.
 *
 * @param priceByOperationId Catalogue price keyed by operationId.
 */
export function esignStepPrice(
  signers: readonly EsignSigner[],
  priceByOperationId: ReadonlyMap<string, number>,
): number {
  return signers
    .filter((signer) => signer.boxCount > 0)
    .reduce(
      (total, signer) => total + (priceByOperationId.get(esignOperationId(signer.signType)) ?? 0),
      0,
    );
}

/** Catalogue operationId for a sign type — how a signer maps onto a priced product. */
export function esignOperationId(signType: string): string {
  switch (signType) {
    case '2':
      return 'Post_Esign_Aadhaar';
    case '3':
      return 'Post_Esign_Dsc';
    default:
      return 'Post_Esign_Electronic';
  }
}

export function signTypeLabel(signType: string): string {
  return SIGN_TYPES.find((t) => t.value === signType)?.label ?? 'Electronic';
}

/**
 * Whether two signer lists say the same thing.
 *
 * Field by field, NOT by comparing JSON: `JSON.stringify` is key-order sensitive, and
 * the two sides come from different places — one parsed by zod (schema order), one
 * built as an object literal. Their keys happened to differ in order, so a
 * stringify comparison reported "changed" on every render, which re-armed the
 * editor's autosave timer forever and meant this step never saved itself.
 */
export function sameEsignSigners(a: readonly EsignSigner[], b: readonly EsignSigner[]): boolean {
  if (a.length !== b.length) return false;

  return a.every((left, index) => {
    const right = b[index];
    return (
      right !== undefined &&
      left.userId === right.userId &&
      left.name === right.name &&
      left.signType === right.signType &&
      left.source === right.source &&
      left.channel === right.channel &&
      left.email === right.email &&
      left.mobile === right.mobile &&
      left.boxCount === right.boxCount
    );
  });
}

/** Anything that would stop this step sending its signature request at run time. */
export function validateEsignSigners(signers: readonly EsignSigner[]): string[] {
  const problems: string[] = [];

  if (signers.length === 0) {
    problems.push('Add at least one signer.');
    return problems;
  }

  const applicants = signers.filter((s) => s.source === 'applicant');
  if (applicants.length > 1) {
    // Only one person runs a session, so two "applicant" signers would both resolve
    // to the same address and the same person would be asked to sign twice.
    problems.push('Only one signer can be the applicant.');
  }

  for (const signer of signers) {
    if (signer.source === 'fixed') {
      if (signer.channel === 'mobile') {
        if (!isMobile(signer.mobile)) {
          problems.push(`${signer.name || 'A signer'} needs a 10-digit mobile number.`);
        }
      } else if (!isEmail(signer.email)) {
        problems.push(`${signer.name || 'A signer'} needs an email address.`);
      }
    }
    if (signer.boxCount === 0) {
      problems.push(`${signer.name || 'A signer'} has no signature placed.`);
    }
  }

  return problems;
}

/** Deliberately permissive — the address is delivered to, not parsed by, us. */
function isEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/**
 * An Indian mobile number, matching what the OTP entry accepts elsewhere in Studio
 * (ApplicantMobileNumber on the server): ten digits starting 6-9, with an optional
 * +91 / 91 / 0 prefix and any punctuation.
 */
function isMobile(value: string): boolean {
  return normalizeMobile(value).length > 0;
}

/**
 * The bare national number to send, or '' when the input isn't one.
 *
 * The gateway accepts `^\+?\d{10,15}$`, but SignCare matches a returning signer against
 * the number stored on their account — so "+91 98765 43210" and "9876543210" must
 * reduce to the same string or the same person gets two accounts.
 */
export function normalizeMobile(value: string): string {
  const digits = value.replace(/\D/g, '');
  const national =
    digits.length === 12 && digits.startsWith('91')
      ? digits.slice(2)
      : digits.length === 11 && digits.startsWith('0')
        ? digits.slice(1)
        : digits;

  return /^[6-9]\d{9}$/.test(national) ? national : '';
}
